import { Formats } from "@portal/hooks/use-formats";
import { Invoice, InvoiceStatusesConfig } from "@portal/types/entities/invoice";
import { GridColumnProps } from "@sinecycle/growcomponents";
import { useMemo } from "react";
import { InvoiceActionPrivileges } from "../InvoiceList";
import { getInvoiceStatusConfig } from "../predicate";
import { DueDateCell } from "./DueDate/DueDateCell";
import { ReferenceCell } from "./Reference/ReferenceCell";

interface InvoiceColumnProps {
  privileges?: InvoiceActionPrivileges;
  invoiceStatusConfig: InvoiceStatusesConfig;
  extra: Formats;
}

function useColumns(props: InvoiceColumnProps) {
  const { invoiceStatusConfig } = props;

  function handleColumns() {
    const columns: GridColumnProps<Invoice> = [
      {
        headerName: "Reference #",
        cellRenderer: ReferenceCell,
        pinned: "left",
        colId: "REFERENCE",
      },
      {
        colId: "ISSUE_DATE",
        headerName: "Issued Date",
        cellRenderer: "dateCell",
        field: "date",
        cellRendererParams: {
          dateCellProps: {
            format: props.extra.dateformat,
          },
        },
        sortable: true,
      },
      {
        colId: "AMOUNT",
        headerName: "Invoice Amount",
        cellRenderer: "amountCell",
        field: "amount",
        cellRendererParams: {
          amountTextProps: {
            locale: props.extra.locale,
          },
        },
        valueGetter: (params) => {
          return {
            amount: params?.data?.amount,
            currency: params?.data?.currency,
          };
        },
        cellClass: "ag-right-aligned-cell",
        headerClass: "ag-right-aligned-header",
      },
      {
        colId: "DUE_AMOUNT",
        headerName: "Invoice Due Amount",
        cellRenderer: "amountCell",
        field: "balance_due_amount",
        cellRendererParams: {
          amountTextProps: {
            locale: props.extra.locale,
          },
        },
        valueGetter: (params) => {
          return {
            amount: params?.data?.balance_due_amount,
            currency: params?.data?.currency,
          };
        },
        cellClass: "ag-right-aligned-cell",
        headerClass: "ag-right-aligned-header",
      },
      {
        colId: "DUE_DATE",
        headerName: "Due Date",
        sortable: true,
        cellRenderer: "dateCell",
        field: "due_date",
        cellRendererParams: {
          dateCellProps: {
            format: props.extra.dateformat,
          },
        },
      },
      {
        colId: "DUE_DAYS",
        headerName: "Due Days",
        field: "due_date",
        cellRendererParams: {
          invoiceStatusConfig,
        },
        cellRenderer: DueDateCell,
      },

      {
        colId: "PTP_DATE",
        headerName: "PTP Date",
        field: "ptp_date",
        cellRenderer: "dateCell",
        cellRendererParams: {
          dateCellProps: {
            format: props.extra.dateformat,
          },
        },
      },
      {
        colId: "STATUS",
        headerName: "Invoice Status",
        field: "status",
        cellRenderer: "statusCell",
        cellRendererParams: {
          statusCellProps: {
            getter: getInvoiceStatusConfig,
          },
        },
      },
    ];
    return columns;
  }

  const defaultColConfigs = useMemo(handleColumns, [
    props.extra.dateformat,
    props.extra.locale,
    invoiceStatusConfig,
  ]);

  return { defaultColConfigs };
}

export { useColumns };
